﻿import { api, isApiErrorStatus } from '@/lib/apiClient';

import type {
  ArcaConfiguration,
  AuditLog,
  AuthorizeInvoicePayload,
  ElectronicInvoice,
  ElectronicInvoiceReport,
  Reminder,
  Supplier,
  SupplierNormalizationPreview,
} from '@/types/domain';
import { earnsLoyaltyPointsByDefault, normalizeProductCategory, PRODUCT_CATEGORY_OPTIONS } from '@/utils/productCategories';
import { calculateProductMultiplier } from '@/utils/productPricing';
import { getProductStockStatus } from '@/utils/productStock';
import { toAppDateTime } from '@/utils/dateTime';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function asText(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asJsonObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getUuidOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return UUID_RE.test(value) ? value : null;
}

function toDateOnly(value: unknown): string {
  const str = asText(value);
  if (!str) return new Date().toISOString().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10);
  return date.toISOString().slice(0, 10);
}

const genId = () => crypto.randomUUID();

function toProductPayload(product: Record<string, unknown>) {
  const status = asText(product.status);
  const price = asNumber(product.salePrice ?? product.price);
  const cost = asNumber(product.costPrice ?? product.cost);
  const taxPercentage = asNumber(product.ivaPercentage, 21);
  const costWithTax = asNumber(product.costPriceWithIVA, cost * (1 + taxPercentage / 100));
  return {
    id: getUuidOrNull(product.id) ?? genId(),
    name: asText(product.name),
    sku: asText((product.internalCode ?? product.barcode ?? product.sku) as string),
    description: asText(product.description),
    price,
    cost,
    quantity: asNumber(product.stock ?? product.quantity),
    category: asText((product.category ?? product.brand) as string),
    brand: asText(product.brand),
    barcode: asText(product.barcode),
    costWithTax,
    multiplier: calculateProductMultiplier(costWithTax, price),
    taxPercentage,
    unit: asText(product.unit, 'unidad'),
    supplier: asText(product.supplier),
    supplierId: getUuidOrNull(product.supplierId),
    minStock: asNumber(product.minStock, 5),
    active: status ? status !== 'inactive' : asBoolean(product.isActive ?? product.active, true),
  };
}

function toLocalDateTime(value: unknown): { date: string; time: string } {
  return toAppDateTime(asText(value));
}

// â”€â”€â”€ Products â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchProducts() {
  const data = await api.get<any[]>('/products');
  return (data || []).map((row) => {
    const stock = asNumber(row.quantity);
    const minStock = asNumber(row.minStock, 5);
    const costPrice = asNumber(row.cost);
    const ivaPercentage = asNumber(row.taxPercentage, 21);
    const costPriceWithIVA = asNumber(row.costWithTax, costPrice * (1 + ivaPercentage / 100));
    const salePrice = asNumber(row.price);
    return {
    id: asText(row.id),
    name: asText(row.name),
    brand: asText(row.brand),
    category: normalizeProductCategory(row.category),
    barcode: asText(row.barcode),
    internalCode: asText(row.sku),
    supplier: asText(row.supplier),
    supplierId: getUuidOrNull(row.supplierId),
    costPrice,
    costPriceWithIVA,
    salePrice,
    price: salePrice,
    multiplier: calculateProductMultiplier(costPriceWithIVA, salePrice),
    stock,
    quantity: stock,
    minStock,
    ivaPercentage,
    unit: asText(row.unit, 'unidad'),
    description: asText(row.description),
    status: getProductStockStatus(stock, minStock, row.active === false ? 'inactive' : undefined),
    expiryDate: '',
    lastPriceUpdate: '',
    isActive: asBoolean(row.active, true),
    active: asBoolean(row.active, true),
    createdAt: asText(row.createdAt),
    updatedAt: asText(row.updatedAt),
    };
  });
}

export async function upsertProduct(product: Record<string, unknown>) {
  const id = getUuidOrNull(product.id);
  const { id: payloadId, ...payload } = toProductPayload(product);
  if (id) {
    try {
      return await api.patch('/products/' + id, payload);
    } catch (e: unknown) {
      if (!isApiErrorStatus(e, 404)) throw e;
      return api.post('/products', { ...payload, id });
    }
  }
  return api.post('/products', { ...payload, id: payloadId });
}

export async function bulkUpsertProducts(products: Record<string, unknown>[]) {
  const payload = products.map(toProductPayload);
  const batchSize = 75;
  let persisted = 0;

  for (let index = 0; index < payload.length; index += batchSize) {
    const batch = payload.slice(index, index + batchSize);
    let lastError: unknown;

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const result = await api.post<{ persisted: number }>('/products/bulk-upsert', {
          products: batch,
        });
        persisted += result.persisted;
        lastError = undefined;
        break;
      } catch (error) {
        lastError = error;
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }
    }

    if (lastError) {
      throw new Error(
        `Se guardaron ${persisted} de ${payload.length} productos antes de que fallara la conexión. Podés reintentar sin generar duplicados.`,
        { cause: lastError }
      );
    }
  }

  return { persisted };
}

export async function updateProductStock(productId: string, stock: number, _status: string) {
  const id = getUuidOrNull(productId);
  if (!id) return;
  return api.patch('/products/' + id, { quantity: stock });
}

export async function deleteProduct(productId: string) {
  const id = getUuidOrNull(productId);
  if (!id) return;
  return api.delete('/products/' + id);
}

// â”€â”€â”€ Clients â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchClients() {
  const data = await api.get<any[]>('/clients');
  return (data || []).map((row) => ({
    id: asText(row.id),
    name: asText(row.name),
    phone: asText(row.phone),
    whatsapp: asText(row.phone),
    email: asText(row.email),
    dni: asText(row.dni),
    address: asText(row.address),
    city: asText(row.city),
    notes: asText(row.notes),
    createdAt: asText(row.createdAt ?? '').slice(0, 10),
    pets: [],
  }));
}

export async function upsertClient(client: Record<string, unknown>) {
  const id = getUuidOrNull(client.id);
  const email = asText(client.email).trim();
  const payload = {
    name: asText(client.name),
    email: email || null,
    phone: asText((client.phone ?? client.whatsapp) as string),
    dni: asText(client.dni),
    address: asText(client.address),
    city: asText(client.city),
    postalCode: asText(client.postalCode),
    notes: asText(client.notes),
  };
  if (id) {
    try {
      return await api.patch('/clients/' + id, payload);
    } catch (e: unknown) {
      if (!isApiErrorStatus(e, 404)) throw e;
      // client exists in frontend but not in DB → create it
      return api.post('/clients', { ...payload, id });
    }
  }
  return api.post('/clients', { ...payload, id: genId() });
}

export async function deleteClient(clientId: string) {
  const id = getUuidOrNull(clientId);
  if (!id) return;
  return api.delete('/clients/' + id);
}

// â”€â”€â”€ Patients â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchPatients() {
  const data = await api.get<any[]>('/patients');
  return (data || []).map((row) => ({
    id: asText(row.id),
    clientId: asText(row.clientId),
    clientName: '',
    name: asText(row.name),
    species: asText(row.species),
    breed: asText(row.breed),
    sex: asText(row.sex),
    birthDate: asText(row.birthDate),
    ageYears: row.ageYears === null || row.ageYears === undefined ? undefined : asNumber(row.ageYears),
    ageMonths: row.ageMonths === null || row.ageMonths === undefined ? undefined : asNumber(row.ageMonths),
    ageIsApproximate: asBoolean(row.ageIsApproximate, !row.birthDate),
    photoUrl: asText(row.photoUrl),
    color: asText(row.color),
    weight: asNumber(row.weight),
    chipNumber: asText(row.microchip),
    castrado: asText(row.castrado, 'No'),
    notes: asText(row.notes),
    createdAt: asText(row.createdAt ?? '').slice(0, 10),
  }));
}

export async function upsertPatient(patient: Record<string, unknown>) {
  const id = getUuidOrNull(patient.id);
  const optionalNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const weight = optionalNumber(patient.weight);
  const bd = asText((patient.birthDate ?? patient.birth_date) as string);
  const payload: Record<string, unknown> = {
    clientId: getUuidOrNull((patient.clientId ?? patient.client_id) as string) ?? '',
    name: asText(patient.name),
    species: asText(patient.species),
    breed: asText(patient.breed),
    sex: asText(patient.sex),
    color: asText(patient.color),
    weight: weight !== null && weight > 0 ? weight : null,
    ageYears: optionalNumber(patient.ageYears),
    ageMonths: optionalNumber(patient.ageMonths),
    ageIsApproximate: patient.ageIsApproximate === undefined ? !bd : patient.ageIsApproximate === true,
    photoUrl: asText(patient.photoUrl),
    microchip: asText((patient.chipNumber ?? patient.microchip) as string),
    notes: asText(patient.notes),
    castrado: asText(patient.castrado, 'No'),
  };
  payload.birthDate = bd || null;
  if (id) {
    try {
      return await api.patch('/patients/' + id, payload);
    } catch (e: unknown) {
      if (!isApiErrorStatus(e, 404)) throw e;
      return api.post('/patients', { ...payload, id });
    }
  }
  return api.post('/patients', { ...payload, id: genId() });
}

export async function deletePatient(patientId: string) {
  const id = getUuidOrNull(patientId);
  if (!id) return;
  return api.delete('/patients/' + id);
}

// â”€â”€â”€ Appointments â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchAppointments() {
  const data = await api.get<any[]>('/appointments');
  return (data || []).map((row) => {
    const dateTime = toAppDateTime(row.scheduledAt || new Date());
    return {
      id: asText(row.id),
      date: dateTime.date,
      time: row.hasTime === false ? '' : dateTime.time,
      hasTime: row.hasTime !== false,
      clientId: asText(row.clientId),
      clientName: asText(row.clientName),
      clientPhone: asText(row.clientPhone ?? row.client?.phone),
      patientId: asText(row.patientId),
      patientName: asText(row.patientName),
      species: '',
      type: asText(row.appointmentType, asText(row.reason, 'otro')),
      status: ({ pending: 'pendiente', confirmed: 'confirmado', completed: 'atendido', cancelled: 'cancelado' } as Record<string, string>)[asText(row.status)] ?? asText(row.status, 'pendiente'),
      reason: asText(row.reason),
      notes: asText(row.notes),
      createdBy: '',
      createdAt: asText(row.createdAt ?? '').slice(0, 10),
    };
  });
}

export async function upsertAppointment(appointment: Record<string, unknown>) {
  const id = getUuidOrNull(appointment.id);
  const clientId = getUuidOrNull((appointment.clientId ?? appointment.client_id) as string);
  const patientId = getUuidOrNull((appointment.patientId ?? appointment.patient_id) as string);
  const clientName = asText(appointment.clientName).trim();
  const patientName = asText(appointment.patientName).trim();
  if (!clientId && !clientName) throw new Error('Selecciona o escribe un cliente.');
  if (!patientId && !patientName) throw new Error('Selecciona o escribe un paciente.');
  const date = asText(appointment.date);
  const hasTime = appointment.hasTime === false ? false : Boolean(asText(appointment.time));
  const time = hasTime ? asText(appointment.time, '09:00') : '12:00';
  const scheduledAt = date
    ? new Date(date + 'T' + time + ':00').toISOString()
    : new Date().toISOString();
  const payload = {
    clientId: clientId || null,
    patientId: patientId || null,
    clientName,
    clientPhone: asText(appointment.clientPhone).trim(),
    patientName,
    scheduledAt,
    hasTime,
    status: ({ pendiente: 'pending', confirmado: 'confirmed', completado: 'completed', atendido: 'completed', cancelado: 'cancelled' } as Record<string, string>)[asText(appointment.status)] ?? asText(appointment.status, 'pending'),
    appointmentType: asText(appointment.type, 'otro'),
    reason: asText((appointment.reason ?? appointment.type) as string),
    notes: asText(appointment.notes),
  };
  if (id) {
    try {
      return await api.patch('/appointments/' + id, payload);
    } catch (e: unknown) {
      if (!isApiErrorStatus(e, 404)) throw e;
      return api.post('/appointments', { ...payload, id });
    }
  }
  return api.post('/appointments', { ...payload, id: genId() });
}

export async function deleteAppointment(appointmentId: string) {
  const id = getUuidOrNull(appointmentId);
  if (!id) return;
  return api.delete('/appointments/' + id);
}

// â”€â”€â”€ Medical Records â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchMedicalRecords() {
  const data = await api.get<any[]>('/medical-records');
  return data.map((r) => ({
    id: asText(r.id), patientId: asText(r.patientId), patientName: asText(r.patient?.name),
    clientName: asText(r.patient?.client?.name), species: asText(r.patient?.species),
    date: asText(r.recordDate).slice(0, 10), type: asText(r.type), vaccineType: asText(r.vaccineType), anamnesis: asText(r.anamnesis),
    diagnosis: asText(r.diagnosis), treatment: asText(r.treatment), prescription: asText(r.prescriptions),
    weight: asNumber(r.weight), temperature: asNumber(r.temperature), notes: asText(r.notes),
    createdBy: asText(r.veterinarian), createdAt: asText(r.createdAt),
  }));
}
export async function upsertMedicalRecord(r: Record<string, unknown>) {
  const id = getUuidOrNull(r.id);
  const payload = { patientId: r.patientId, recordDate: toDateOnly(r.date), type: r.type, vaccineType: r.vaccineType || null, anamnesis: r.anamnesis,
    diagnosis: r.diagnosis, treatment: r.treatment, prescriptions: r.prescription, weight: r.weight || null,
    temperature: r.temperature || null, notes: r.notes, veterinarian: r.createdBy };
  if (!id) return api.post('/medical-records', payload);
  try {
    return await api.patch('/medical-records/' + id, payload);
  } catch (error: unknown) {
    if (!isApiErrorStatus(error, 404)) throw error;
    return api.post('/medical-records', { ...payload, id });
  }
}

// â”€â”€â”€ Cash Registers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function toCashRegister(r: any) {
  const movements = Array.isArray(r.movements)
    ? r.movements.filter((movement: any) => asText(movement.status, 'activo') === 'activo')
    : [];
  const totalByType = (type: string) => movements
    .filter((movement: any) => asText(movement.type) === type)
    .reduce((sum: number, movement: any) => sum + asNumber(movement.amount), 0);
  return {
    id: asText(r.id),
    status: r.status === 'open' ? 'abierta' : r.status === 'closed' ? 'cerrada' : asText(r.status),
    openingAmount: asNumber(r.openingAmount),
    closedAmount: asNumber(r.closingAmount),
    expectedAmount: asNumber(r.expectedAmount),
    openedBy: asText(r.openedBy),
    closedBy: asText(r.closedBy),
    openedAt: asText(r.openedAt),
    closedAt: asText(r.closedAt),
    note: asText(r.notes),
    totalSales: totalByType('sale'),
    totalIncome: totalByType('in'),
    totalExpense: totalByType('out'),
    byPaymentMethod: asJsonObject(r.breakdown),
    closingBreakdown: asJsonObject(r.breakdown),
    closingObservations: asText(r.notes),
    closingDifference: asNumber(r.closingAmount) - asNumber(r.expectedAmount),
  };
}

function toCashRegisterPayload(r: Record<string, unknown>) {
  return { status: r.status === 'abierta' ? 'open' : r.status === 'cerrada' ? 'closed' : r.status,
    openingAmount: r.openingAmount, closingAmount: r.closedAmount, expectedAmount: r.expectedAmount,
    breakdown: r.closingBreakdown ?? r.byPaymentMethod, notes: r.note ?? r.closingObservations,
    openedBy: r.openedBy, closedBy: r.closedBy, openedAt: r.openedAt || undefined, closedAt: r.closedAt || undefined };
}

export async function fetchCashRegisters() {
  const data = await api.get<any[]>('/cash-registers');
  return data.map(toCashRegister);
}

export async function createCashRegister(r: Record<string, unknown>) {
  const persisted = await api.post<any>('/cash-registers', {
    ...toCashRegisterPayload(r),
    id: getUuidOrNull(r.id) ?? genId(),
  });
  return toCashRegister(persisted);
}

export async function updateCashRegister(id: string, r: Record<string, unknown>) {
  const registerId = getUuidOrNull(id);
  if (!registerId) throw new Error('La caja seleccionada no es válida. Recargá la pantalla e intentá nuevamente.');
  const persisted = await api.patch<any>('/cash-registers/' + registerId, toCashRegisterPayload(r));
  return toCashRegister(persisted);
}

// â”€â”€â”€ Cash Movements â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchCashMovements() {
  const data = await api.get<any[]>('/cash-registers/movements/all');
  return data.map((m) => ({ id: asText(m.id), cashRegisterId: asText(m.cashRegisterId),
    type: m.type === 'sale' ? 'venta' : m.type === 'in' ? 'ingreso' : m.type === 'out' ? 'egreso' : asText(m.type), amount: asNumber(m.amount),
    concept: asText(m.concept), note: asText(m.notes), paymentMethod: asText(m.paymentMethod),
    voucher: asText(m.voucher), voucherNumber: asText(m.voucher), origin: asText(m.origin),
    status: asText(m.status, 'activo'), createdBy: asText(m.createdBy), createdAt: asText(m.createdAt) }));
}
export async function insertCashMovement(m: Record<string, unknown>) {
  return api.post('/cash-registers/movements', { id: getUuidOrNull(m.id), cashRegisterId: m.cashRegisterId,
    type: m.type === 'venta' ? 'sale' : m.type === 'ingreso' ? 'in' : 'out', amount: m.amount, concept: m.concept,
    notes: m.note, paymentMethod: m.paymentMethod, voucher: m.voucherNumber ?? m.voucher,
    origin: m.origin, status: m.status, createdBy: m.createdBy });
}
export async function updateCashMovement(id: string, u: Record<string, unknown>) { return api.patch('/cash-registers/movements/' + id, u); }

// â”€â”€â”€ Sales â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchSales() {
  const data = await api.get<any[]>('/sales');
  return (data || []).map((sale) => {
    const explicitClientName = asText(sale.client?.name ?? sale.clientName);
    const created = toLocalDateTime(sale.createdAt);
    return {
    id: asText(sale.referenceNumber, asText(sale.id)),
    dbId: asText(sale.id),
    createdAt: asText(sale.createdAt),
    date: created.date,
    time: created.time,
    clientName: explicitClientName || asText(sale.notes, 'Consumidor final'),
    clientId: asText(sale.clientId),
    seller: asText(sale.seller, 'Dr. Martín López'),
    sellerId: asText(sale.sellerId, 'user-001'),
    paymentMethod: asText(sale.paymentMethod, 'Efectivo'),
    items: (sale.items || []).map((item: any) => {
      const isManualProduct = asBoolean(item.isManualProduct);
      return {
      saleItemId: asText(item.id),
      productId: isManualProduct ? `manual-${asText(item.id, crypto.randomUUID())}` : asText(item.productId),
      productName: asText(item.product?.name ?? item.productName ?? ''),
      brand: asText(item.brand),
      category: asText(item.product?.category),
      quantity: asNumber(item.quantity, 1),
      unitPrice: asNumber(item.unitPrice),
      discountPercentage: asNumber(item.discountPercentage),
      discountAmount: asNumber(item.discountAmount),
      grossSubtotal: asNumber(item.grossSubtotal, asNumber(item.subtotal)),
      originalPrice: undefined as number | undefined,
      isPriceEdited: false,
      priceEditReason: '',
      subtotal: asNumber(item.subtotal),
      isManualProduct,
    };
    }),
    subtotal: asNumber(sale.subtotal),
    discount: asNumber(sale.discountAmount),
    lineDiscount: asNumber(sale.lineDiscountAmount),
    surcharge: asNumber(sale.surchargeAmount),
    total: asNumber(sale.total),
    status: sale.status === 'completed' ? 'completada' : sale.status === 'cancelled' ? 'anulada' : asText(sale.status, 'completada'),
    hasEditedPrices: false,
    notes: explicitClientName ? asText(sale.notes) : '',
    cancelledBy: '',
    cancellationReason: '',
  };
  });
}

export async function persistCompleteSaleToAPI(payload: {
  preSaleId?: string;
  referenceNumber: string;
  seller: string;
  sellerId: string;
  clientId?: string | null;
  clientName?: string;
  paymentMethod: string;
  discountAmount: number;
  discountPercentage: number;
  surchargeAmount: number;
  surchargePercentage: number;
  notes?: string;
  cashRegisterId?: string;
  cashMovements: Array<{
    id: string;
    paymentMethod: string;
    amount: number;
  }>;
  items: Array<{
    productId?: string | null;
    productName?: string;
    brand?: string;
    quantity: number;
    unitPrice: number;
    discountPercentage?: number;
    subtotal: number;
    isManualProduct?: boolean;
  }>;
  accountReceivable?: { initialPayment: number; dueDate?: string; notes?: string };
}) {
  return api.post<any>('/sales', payload);
}

// ─── Preventas ─────────────────────────────────────────────────────────────
export function fetchPreSales(filters: { search?: string; status?: string; from?: string; to?: string } = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  return api.get<import('@/types/domain').PreSale[]>(`/pre-sales${params.size ? `?${params}` : ''}`);
}

export function createPreSale(payload: Record<string, unknown>) {
  return api.post<import('@/types/domain').PreSale>('/pre-sales', payload);
}

export function updatePreSale(id: string, payload: Record<string, unknown>) {
  return api.patch<import('@/types/domain').PreSale>(`/pre-sales/${id}`, payload);
}

export function cancelPreSale(id: string) {
  return api.post<import('@/types/domain').PreSale>(`/pre-sales/${id}/cancel`, {});
}

// ─── Finanzas: Caja Mayor, gastos, cuentas corrientes y devoluciones ──────
export function fetchMajorCash(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return api.get<import('@/types/domain').MajorCashReport>(`/finance/major-cash${params.size ? `?${params}` : ''}`);
}

export function fetchReceivables(filters: { search?: string; status?: string } = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  return api.get<import('@/types/domain').ReceivableAccount[]>(`/finance/receivables${params.size ? `?${params}` : ''}`);
}

export function registerReceivablePayment(id: string, payload: { amount: number; paymentMethod: string; voucher?: string; notes?: string }) {
  return api.post<import('@/types/domain').ReceivableAccount>(`/finance/receivables/${id}/payments`, payload);
}

export function fetchExpenses(filters: { search?: string; status?: string; from?: string; to?: string } = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  return api.get<import('@/types/domain').OperatingExpense[]>(`/finance/expenses${params.size ? `?${params}` : ''}`);
}

export function createExpense(payload: Record<string, unknown>) {
  return api.post<import('@/types/domain').OperatingExpense>('/finance/expenses', payload);
}

export function registerExpensePayment(id: string, payload: { amount: number; paymentMethod: string; voucher?: string; notes?: string }) {
  return api.post<import('@/types/domain').OperatingExpense>(`/finance/expenses/${id}/payments`, payload);
}

export function fetchReturns(filters: { saleId?: string; from?: string; to?: string } = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  return api.get<import('@/types/domain').ReturnOperation[]>(`/finance/returns${params.size ? `?${params}` : ''}`);
}

export function createReturnOperation(payload: Record<string, unknown>) {
  return api.post<import('@/types/domain').ReturnOperation>('/finance/returns', payload);
}

export async function fetchReminderArchive(): Promise<Reminder[]> {
  const data = await api.get<any[]>('/reminders?includeArchived=true');
  return (data || []).filter((item) => item.archivedAt).map(mapReminder);
}

export function uploadPatientPhoto(patientId: string, file: File) {
  return api.upload<import('@/types/domain').FileAttachment>(`/attachments/patient/${patientId}/photo`, file);
}

export function removePatientPhoto(patientId: string) {
  return api.delete(`/attachments/patient/${patientId}/photo`);
}

export function uploadMedicalAttachment(recordId: string, file: File) {
  return api.upload<import('@/types/domain').FileAttachment>(`/attachments/medical-record/${recordId}`, file);
}

export function fetchAttachments(ownerType: 'patient_photo' | 'medical_record', ownerId: string) {
  return api.get<import('@/types/domain').FileAttachment[]>(`/attachments?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function deleteAttachment(id: string) { return api.delete(`/attachments/${id}`); }
export function fetchAttachmentBlob(url: string) { return api.blob(url); }

export async function updateSale(id: string, payload: {
  clientName?: string;
  paymentMethod?: string;
  notes?: string;
  discountAmount?: number;
  surchargeAmount?: number;
  items?: Array<{
    productId?: string | null;
    productName?: string;
    brand?: string;
    quantity: number;
    unitPrice: number;
    discountPercentage?: number;
    subtotal: number;
    isManualProduct?: boolean;
  }>;
}) {
  const saleId = getUuidOrNull(id);
  if (!saleId) throw new Error('La venta todavía no terminó de sincronizarse. Intentá nuevamente.');
  return api.patch<any>('/sales/' + saleId, payload);
}

// ─── Facturación electrónica ARCA ──────────────────────────────────────────
export function fetchArcaConfiguration(): Promise<ArcaConfiguration> {
  return api.get('/invoices/configuration');
}

export function checkArcaConnection(): Promise<Record<string, unknown>> {
  return api.post('/invoices/check-connection', {});
}

export function fetchInvoiceForSale(saleId: string): Promise<ElectronicInvoice | null> {
  const id = getUuidOrNull(saleId);
  if (!id) return Promise.resolve(null);
  return api.get('/invoices/sale/' + id);
}

export function fetchElectronicInvoiceReport(from?: string, to?: string): Promise<ElectronicInvoiceReport> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const suffix = params.size ? `?${params.toString()}` : '';
  return api.get('/invoices/report' + suffix);
}

export function authorizeInvoice(
  saleId: string,
  payload: AuthorizeInvoicePayload,
): Promise<ElectronicInvoice> {
  const id = getUuidOrNull(saleId);
  if (!id) throw new Error('La venta todavía no terminó de sincronizarse. Esperá unos segundos e intentá nuevamente.');
  return api.post('/invoices/sale/' + id + '/authorize', payload);
}

// â”€â”€â”€ Payment Methods â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchPaymentMethods() {
  const data = await api.get<any[]>('/payment-methods');
  return data.map((m) => ({ id: asText(m.id), name: asText(m.name), type: asText(m.type, 'efectivo'),
    discount: asNumber(m.discountPercentage), surcharge: asNumber(m.surchargePercentage),
    discountPercent: asNumber(m.discountPercentage), discountFixed: asNumber(m.discountFixed),
    surchargePercent: asNumber(m.surchargePercentage), surchargeFixed: asNumber(m.surchargeFixed),
    active: asBoolean(m.active, true), isActive: asBoolean(m.active, true), order: Math.max(1, asNumber(m.displayOrder, 1)) }));
}
export async function upsertPaymentMethod(m: Record<string, unknown>) {
  const id = getUuidOrNull(m.id);
  const payload = { name: m.name, type: m.type, description: m.description,
    discountPercentage: m.discountPercent ?? m.discount, discountFixed: m.discountFixed,
    surchargePercentage: m.surchargePercent ?? m.surcharge, surchargeFixed: m.surchargeFixed,
    active: m.isActive ?? m.active, displayOrder: Math.max(1, asNumber(m.order, 1)) };
  if (!id) return api.post('/payment-methods', payload);
  try {
    return await api.patch('/payment-methods/' + id, payload);
  } catch (error: unknown) {
    if (!isApiErrorStatus(error, 404)) throw error;
    return api.post('/payment-methods', { ...payload, id });
  }
}
export async function deletePaymentMethod(id: string) { return api.delete('/payment-methods/' + id); }

// ─── Product Categories ──────────────────────────────────────────
export async function fetchProductCategories() {
  try {
    const data = await api.get<any[]>('/product-categories?includeInactive=true');
    return (data || []).map((category) => ({
      id: asText(category.id),
      name: asText(category.name),
      earnsLoyaltyPoints: asBoolean(category.earnsLoyaltyPoints, true),
      active: asBoolean(category.active, true),
      productCount: asNumber(category.productCount),
      createdAt: asText(category.createdAt),
      updatedAt: asText(category.updatedAt),
    }));
  } catch (error) {
    console.warn('No se pudieron cargar las categorías configurables; se usa el listado local.', error);
    return PRODUCT_CATEGORY_OPTIONS.map((name, index) => ({
      id: `fallback-category-${index}`,
      name,
      earnsLoyaltyPoints: earnsLoyaltyPointsByDefault(name),
      active: true,
      productCount: 0,
      createdAt: '',
      updatedAt: '',
    }));
  }
}

export function createProductCategory(category: { name: string; earnsLoyaltyPoints: boolean }) {
  return api.post('/product-categories', category);
}

export function updateProductCategory(id: string, category: { name?: string; earnsLoyaltyPoints?: boolean; active?: boolean }) {
  return api.patch('/product-categories/' + id, category);
}

export function deactivateProductCategory(id: string) {
  return api.delete('/product-categories/' + id);
}

// ─── Suppliers ──────────────────────────────────────────────────
export async function fetchSuppliers(): Promise<Supplier[]> {
  const data = await api.get<any[]>('/suppliers?includeInactive=true');
  return (data || []).map((supplier) => ({
    id: asText(supplier.id),
    name: asText(supplier.name),
    taxId: asText(supplier.taxId),
    contactName: asText(supplier.contactName),
    phone: asText(supplier.phone),
    email: asText(supplier.email),
    address: asText(supplier.address),
    notes: asText(supplier.notes),
    active: asBoolean(supplier.active, true),
    createdAt: asText(supplier.createdAt),
    updatedAt: asText(supplier.updatedAt),
  }));
}

export function createSupplier(payload: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>) {
  return api.post<Supplier>('/suppliers', payload);
}

export function updateSupplier(id: string, payload: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>) {
  return api.patch<Supplier>(`/suppliers/${id}`, payload);
}

export function deactivateSupplier(id: string) {
  return api.delete(`/suppliers/${id}`);
}

export function previewSupplierNormalization() {
  return api.get<SupplierNormalizationPreview>('/suppliers/normalization/preview');
}

export function applySupplierNormalization() {
  return api.post<{ updated: number; skippedAmbiguous: number; unmatchedCount: number }>('/suppliers/normalization/apply', { confirm: true });
}

// â”€â”€â”€ Stock Movements â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export async function fetchStockMovements() {
  const data = await api.get<any[]>('/stock-movements');
  return data.map((m) => ({ id: asText(m.id), productId: asText(m.productId), productName: asText(m.product?.name),
    type: m.type === 'in' ? 'ingreso' : m.type === 'out' ? 'egreso' : m.type === 'adjustment' ? 'ajuste' : asText(m.type), quantity: asNumber(m.quantity),
    unitCostWithIVA: asNumber(m.unitCost), unitCostWithoutIVA: asNumber(m.unitCostWithoutTax, m.unitCost),
    totalCost: asNumber(m.unitCostWithoutTax, m.unitCost) * asNumber(m.quantity),
    previousStock: asNumber(m.previousStock), newStock: asNumber(m.newStock), reason: asText(m.reason),
    categorySnapshot: asText(m.categorySnapshot, m.product?.category), supplierSnapshot: asText(m.supplierSnapshot, m.product?.supplier),
    createdBy: asText(m.createdBy), createdAt: asText(m.createdAt) }));
}
export async function insertStockMovement(m: Record<string, unknown>) {
  const type = asText(m.type);
  const persistedType = ({ ingreso: 'in', egreso: 'out', ajuste: 'adjustment' } as Record<string, string>)[type] || type || 'adjustment';
  return api.post('/stock-movements', { id: getUuidOrNull(m.id), productId: m.productId,
    type: persistedType, quantity: m.quantity,
    reason: m.reason, notes: m.notes, unitCost: m.unitCostWithoutIVA ?? m.unitCostWithIVA,
    unitCostWithoutTax: m.unitCostWithoutIVA ?? m.unitCostWithIVA,
    categorySnapshot: m.categorySnapshot, supplierSnapshot: m.supplierSnapshot, previousStock: m.previousStock,
    newStock: m.newStock, createdBy: m.createdBy });
}

// â”€â”€â”€ Audit Logs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function mapAuditLog(e: any): AuditLog {
  return {
    id: asText(e.id),
    userId: asText(e.userId),
    userName: asText(e.userName),
    action: asText(e.action),
    entity: asText(e.entity),
    entityId: asText(e.entityId),
    details: asJsonObject(e.newData ?? e.details),
    createdAt: asText(e.createdAt),
  };
}

export interface AuditLogPage {
  items: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  action?: string;
  actions?: string[];
  from?: string;
  to?: string;
}

export async function fetchAuditLogPage(query: AuditLogQuery = {}): Promise<AuditLogPage> {
  const params = new URLSearchParams();
  params.set('page', String(query.page || 1));
  params.set('pageSize', String(query.pageSize || 50));
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.action) params.set('action', query.action);
  if (query.actions?.length) params.set('actions', query.actions.join(','));
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const data = await api.get<any>(`/audit-logs?${params.toString()}`);
  return {
    items: (data.items || []).map(mapAuditLog),
    total: asNumber(data.total),
    page: asNumber(data.page, 1),
    pageSize: asNumber(data.pageSize, 50),
    totalPages: asNumber(data.totalPages, 1),
  };
}

export async function fetchAuditLogs(): Promise<AuditLog[]> {
  const first = await fetchAuditLogPage({ page: 1, pageSize: 100 });
  const result = [...first.items];
  for (let page = 2; page <= first.totalPages; page += 1) {
    const next = await fetchAuditLogPage({ page, pageSize: 100 });
    result.push(...next.items);
  }
  return result;
}

export interface AuditSummary {
  total: number;
  users: number;
  byAction: Array<{ label: string; value: number }>;
  byEntity: Array<{ label: string; value: number }>;
  byUser: Array<{ label: string; value: number }>;
  recent: AuditLog[];
}

export async function fetchAuditSummary(from?: string, to?: string): Promise<AuditSummary> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const suffix = params.size ? `?${params.toString()}` : '';
  const data = await api.get<any>(`/audit-logs/summary${suffix}`);
  return {
    total: asNumber(data.total),
    users: asNumber(data.users),
    byAction: (data.byAction || []).map((item: any) => ({ label: asText(item.label), value: asNumber(item.value) })),
    byEntity: (data.byEntity || []).map((item: any) => ({ label: asText(item.label), value: asNumber(item.value) })),
    byUser: (data.byUser || []).map((item: any) => ({ label: asText(item.label), value: asNumber(item.value) })),
    recent: (data.recent || []).map(mapAuditLog),
  };
}
export async function insertAuditLog(e: Record<string, unknown>) {
  return api.post('/audit-logs', { id: getUuidOrNull(e.id), userId: e.userId, userName: e.userName, action: e.action, entity: e.entity,
    entityId: getUuidOrNull(e.entityId) ?? null, newData: e.details, notes: '' });
}

// ─── Reminders ───────────────────────────────────────────────────────────────
function mapReminder(row: any): Reminder {
  return {
    id: asText(row.id),
    clientId: asText(row.clientId),
    clientName: asText(row.client?.name ?? row.clientName),
    patientId: asText(row.patientId),
    patientName: asText(row.patient?.name ?? row.patientName),
    appointmentId: asText(row.appointmentId) || undefined,
    medicalRecordId: asText(row.medicalRecordId) || undefined,
    type: asText(row.type),
    channel: asText(row.channel, 'email') as Reminder['channel'],
    emailDestination: asText(row.emailDestination),
    phoneDestination: asText(row.phoneDestination),
    appointmentDate: asText(row.appointmentDate).slice(0, 10),
    appointmentTime: asText(row.appointmentTime),
    scheduledSendDate: asText(row.scheduledSendDate).slice(0, 10),
    subject: asText(row.subject),
    message: asText(row.message),
    status: asText(row.status, 'pendiente') as Reminder['status'],
    createdAt: asText(row.createdAt),
    updatedAt: asText(row.updatedAt),
    sentAt: asText(row.sentAt) || undefined,
    errorMessage: asText(row.errorMessage) || undefined,
    cancelledAt: asText(row.cancelledAt) || undefined,
    cancelledBy: asText(row.cancelledBy) || undefined,
    archivedAt: asText(row.archivedAt) || undefined,
    archiveReason: asText(row.archiveReason) || undefined,
  };
}

export async function fetchReminders(): Promise<Reminder[]> {
  const data = await api.get<any[]>('/reminders');
  return (data || []).map(mapReminder);
}

function toReminderPayload(reminder: Record<string, unknown>) {
  return {
    id: getUuidOrNull(reminder.id) ?? genId(),
    clientId: getUuidOrNull(reminder.clientId) ?? asText(reminder.clientId),
    patientId: getUuidOrNull(reminder.patientId) ?? asText(reminder.patientId),
    appointmentId: getUuidOrNull(reminder.appointmentId) ?? null,
    medicalRecordId: getUuidOrNull(reminder.medicalRecordId) ?? null,
    type: asText(reminder.type),
    channel: asText(reminder.channel, 'email'),
    emailDestination: asText(reminder.emailDestination),
    phoneDestination: asText(reminder.phoneDestination),
    appointmentDate: toDateOnly(reminder.appointmentDate),
    appointmentTime: asText(reminder.appointmentTime),
    scheduledSendDate: toDateOnly(reminder.scheduledSendDate),
    subject: asText(reminder.subject),
    message: asText(reminder.message),
    status: asText(reminder.status, 'pendiente'),
    sentAt: asText(reminder.sentAt) || null,
    errorMessage: asText(reminder.errorMessage) || null,
    cancelledAt: asText(reminder.cancelledAt) || null,
    cancelledBy: asText(reminder.cancelledBy) || null,
  };
}

export async function upsertReminder(reminder: Record<string, unknown>) {
  const payload = toReminderPayload(reminder);
  const id = getUuidOrNull(reminder.id);
  if (id) {
    try {
      return await api.patch('/reminders/' + id, payload);
    } catch (error: unknown) {
      if (!isApiErrorStatus(error, 404)) throw error;
      return api.post('/reminders', payload);
    }
  }
  return api.post('/reminders', payload);
}

export async function createReminder(reminder: Record<string, unknown>): Promise<Reminder> {
  const saved = await api.post<any>('/reminders', toReminderPayload(reminder));
  return mapReminder(saved);
}

export async function updateReminder(id: string, update: Record<string, unknown>) {
  const reminderId = getUuidOrNull(id);
  if (!reminderId) return;
  return api.patch('/reminders/' + reminderId, update);
}

export async function sendReminderEmail(id: string): Promise<Reminder> {
  const reminderId = getUuidOrNull(id);
  if (!reminderId) throw new Error('Recordatorio invalido.');
  const data = await api.post<any>('/reminders/' + reminderId + '/send', {});
  return mapReminder(data);
}

export async function deleteReminder(id: string) {
  const reminderId = getUuidOrNull(id);
  if (!reminderId) return;
  return api.delete('/reminders/' + reminderId);
}
